import { useMemo, useState } from "react";
import { LuPlus, LuSearch, LuTrash2, LuX } from "react-icons/lu";

function formatRelativeTime(iso) {
    if (!iso) return "";
    const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function ChatHistoryList({ sessions, activeSessionId, isLoading, onSelect, onCreate, onDelete }) {
    const [showSearch, setShowSearch] = useState(false);
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? sessions.filter((s) => (s.title || "").toLowerCase().includes(q)) : sessions;
    }, [sessions, query]);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <button
                type="button"
                onClick={onCreate}
                className="mb-4 flex w-full items-center justify-center gap-2 rounded-full border border-primary-200 bg-white px-4 py-2 font-Mulish text-sm text-primary-700 transition-colors hover:bg-primary-50"
            >
                <LuPlus size={16} />
                <span>New chat</span>
            </button>

            <div className="mb-1.5 flex items-center justify-between px-1">
                <span className="font-Mulish text-xs font-bold uppercase tracking-[0.12em] text-graphite">Chats</span>
                <button
                    type="button"
                    onClick={() => {
                        setShowSearch((v) => !v);
                        if (showSearch) setQuery("");
                    }}
                    title="Search chats"
                    className="rounded-md p-1 text-[var(--text-tertiary)] transition-colors hover:bg-primary-50 hover:text-primary-700"
                >
                    {showSearch ? <LuX size={14} /> : <LuSearch size={14} />}
                </button>
            </div>

            {showSearch && (
                <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search chats..."
                    className="mb-2 w-full rounded-lg border border-[var(--border-primary)] bg-[var(--card-bg)] px-2.5 py-1.5 text-xs text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
            )}

            <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                {isLoading ? (
                    <p className="px-2 py-3 text-xs text-[var(--text-tertiary)]">Loading...</p>
                ) : filtered.length === 0 ? (
                    <div className="rounded-2xl bg-primary-50 px-4 py-6 text-center font-Mulish">
                        <p className="text-sm font-semibold text-secondary">{query ? "No matching chats" : "No chats yet"}</p>
                        {!query && <p className="mt-1 text-xs text-graphite">Your chats will show up here</p>}
                    </div>
                ) : (
                    filtered.map((session) => {
                        const active = session.id === activeSessionId;
                        return (
                            <div
                                key={session.id}
                                onClick={() => onSelect(session.id)}
                                className={`group flex cursor-pointer items-center gap-2 border-l-2 py-2 pl-3 pr-2 font-Mulish text-sm transition-colors ${
                                    active
                                        ? "border-primary-500 font-semibold text-secondary"
                                        : "border-transparent text-graphite hover:bg-hover-tint hover:text-secondary"
                                }`}
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="truncate">{session.title || "New chat"}</p>
                                    <p className="text-[11px] text-[var(--text-tertiary)]">
                                        {formatRelativeTime(session.updated_at || session.created_at)}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(session.id);
                                    }}
                                    title="Delete chat"
                                    className="shrink-0 rounded-md p-1 text-[var(--text-tertiary)] transition-all hover:bg-primary-50 hover:text-primary-700 focus-visible:opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                    <LuTrash2 size={13} />
                                </button>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
